import { useState, useEffect, useCallback } from 'react'
import { TextField } from '@mui/material'
import { Control, Controller, FieldPath, FieldValues, ControllerRenderProps } from 'react-hook-form'
import {
  getFieldWidth,
  formatNumberWithSuffix,
  parseNumber,
  NumberFieldType,
  TextSize,
  NUMBER_FIELD_TYPES,
} from '../NumberControl'

/**
 * field.valueを安全に数値またはnullに変換する型ガード関数
 * React Hook Formのfield.valueは様々な型になりうるため、防御的に処理する
 */
function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }
  // 文字列の場合はパースを試みる
  if (typeof value === 'string') {
    const parsed = parseNumber({ value })
    return parsed
  }
  return null
}

/**
 * NumberFieldコンポーネントのProps
 *
 * 使用パターン:
 * 1. name + control を両方指定: React Hook Form連携モード（入力可能）
 * 2. value のみ指定: 表示専用モード
 * 3. name/control が片方のみまたはundefined: 実行時に非制御モードとして動作
 *
 * 注意: 型安全性のため、制御モードで使用する場合は name と control を必ず指定してください
 */
interface NumberFieldProps<T extends FieldValues = FieldValues> {
  readonly type?: NumberFieldType
  readonly size?: TextSize
  readonly isReadOnly?: boolean
  readonly placeholder?: string
  readonly name?: FieldPath<T>
  readonly control?: Control<T>
  readonly error?: string
  readonly value?: number
  readonly suffix?: string
  readonly disabled?: boolean
}

/**
 * 制御モードかどうかを判定する型ガード
 * name と control が両方存在する場合のみ true を返す
 */
function isControlledMode<T extends FieldValues>(
  name: FieldPath<T> | undefined,
  control: Control<T> | undefined
): name is FieldPath<T> {
  return name !== undefined && control !== undefined
}

/** NumberFieldInnerコンポーネントのProps */
interface NumberFieldInnerProps<T extends FieldValues> {
  readonly field: ControllerRenderProps<T, FieldPath<T>>
  readonly thousandSeparator: boolean
  readonly size?: TextSize
  readonly isReadOnly?: boolean
  readonly placeholder?: string
  readonly error?: string
  readonly suffix?: string
  readonly disabled?: boolean
}

/**
 * 数値入力の内部コンポーネント
 * Controllerのrender内でhooksを使用するため、別コンポーネントに分離
 */
function NumberFieldInner<T extends FieldValues>({
  field,
  thousandSeparator,
  size,
  isReadOnly,
  placeholder,
  error,
  suffix,
  disabled,
}: NumberFieldInnerProps<T>): JSX.Element {
  // 型安全なfield.value変換
  const initialValue = toNumberOrNull(field.value)
  const [displayValue, setDisplayValue] = useState<string>(() =>
    formatNumberWithSuffix({ value: initialValue, thousandSeparator, suffix })
  )

  // field.valueが外部から変更された場合に表示値を同期
  useEffect(() => {
    const numericValue = toNumberOrNull(field.value)
    const formatted = formatNumberWithSuffix({ value: numericValue, thousandSeparator, suffix })
    setDisplayValue(formatted)
  }, [field.value, thousandSeparator, suffix])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    // onChangeでは値を変換せず、そのまま表示（IME対応）
    setDisplayValue(e.target.value)
  }, [])

  const handleBlur = useCallback((): void => {
    // onBlurで全角→半角変換、数値パース、フォーマット適用
    // suffix付きの値からsuffixを除去してパース
    const parsed = parseNumber({ value: displayValue, suffix })
    const formatted = formatNumberWithSuffix({ value: parsed, thousandSeparator, suffix })
    setDisplayValue(formatted)
    field.onChange(parsed)
    field.onBlur()
  }, [displayValue, thousandSeparator, suffix, field])

  const hasError = error !== undefined

  return (
    <TextField
      inputRef={field.ref}
      size="small"
      placeholder={placeholder}
      sx={{ width: getFieldWidth({ size }), textAlign: 'right' }}
      InputProps={{
        readOnly: isReadOnly,
        inputProps: { style: { textAlign: 'right' } },
      }}
      error={hasError}
      helperText={error ?? ''}
      disabled={disabled}
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}

/**
 * 数値入力/表示コンポーネント
 *
 * react-number-formatを使用せず、onBlurのみで値の変換・フォーマットを行う。
 * これによりIME入力時の問題を回避する。
 *
 * 使用パターン:
 * 1. name + control: React Hook Form連携モード（入力可能）
 * 2. valueのみ: 表示専用モード
 */
export default function NumberField<T extends FieldValues = FieldValues>({
  type,
  size,
  isReadOnly,
  placeholder,
  name,
  control,
  error,
  value,
  suffix,
  disabled,
}: NumberFieldProps<T>): JSX.Element {
  const thousandSeparator = type === NUMBER_FIELD_TYPES.AMOUNT

  // 型ガードを使用した制御モード判定
  // name と control が両方存在する場合のみ Controller を使用
  if (isControlledMode(name, control)) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <NumberFieldInner
            field={field}
            thousandSeparator={thousandSeparator}
            size={size}
            isReadOnly={isReadOnly}
            placeholder={placeholder}
            error={error}
            suffix={suffix}
            disabled={disabled}
          />
        )}
      />
    )
  }

  // 非制御モード（表示専用）
  const formattedValue = formatNumberWithSuffix({ value: value ?? null, thousandSeparator, suffix })

  return (
    <TextField
      size="small"
      sx={{ width: getFieldWidth({ size }), textAlign: 'right' }}
      InputProps={{
        readOnly: isReadOnly,
        inputProps: { style: { textAlign: 'right' } },
      }}
      value={formattedValue}
      disabled={disabled}
    />
  )
}
